import React from 'react';
import { Slide, FadeIn, SlideHeading, StaggerContainer, StaggerItem, Card } from './Shared';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MapPin, Factory, Zap, Truck, Package, DollarSign, Clock, TrendingUp, Handshake } from 'lucide-react';
export const Slide11StrategicLocation = () => <Slide id="slide-11" bg="white" data-id="element-19">
    <SlideHeading tag="Strategic Location Edge" title="Supply Chain Dominance" subtitle="San Diego HQ provides unparalleled logistical advantages." data-id="element-20" />

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8 items-center" data-id="element-21">
      <FadeIn delay={0.2} className="lg:col-span-5" data-id="element-22">
        <div className="space-y-12" data-id="element-23">
          <div className="flex gap-6" data-id="element-24">
            <div className="w-16 h-16 bg-black flex items-center justify-center shrink-0" data-id="element-25">
              <MapPin className="w-8 h-8 text-white" data-id="element-26" />
            </div>
            <div data-id="element-27">
              <h4 className="text-2xl font-serif font-bold text-black uppercase mb-3" data-id="element-28">
                Border Proximity
              </h4>
              <p className="text-slate-600 text-lg" data-id="element-29">
                Located minutes from the U.S. / Mexico border, enabling seamless
                cross-border logistics and rapid access to nearshore resources.
              </p>
            </div>
          </div>
          <div className="flex gap-6" data-id="element-30">
            <div className="w-16 h-16 bg-black flex items-center justify-center shrink-0" data-id="element-31">
              <Factory className="w-8 h-8 text-white" data-id="element-32" />
            </div>
            <div data-id="element-33">
              <h4 className="text-2xl font-serif font-bold text-black uppercase mb-3" data-id="element-34">
                Supplier Adjacency
              </h4>
              <p className="text-slate-600 text-lg" data-id="element-35">
                Situated just one block from a major Southern California blank
                apparel supplier holding{' '}
                <strong className="text-black font-bold" data-id="element-36">
                  6,000,000+ garments
                </strong>{' '}
                in inventory.
              </p>
            </div>
          </div>
        </div>
      </FadeIn>

      <div className="lg:col-span-7" data-id="element-37">
        <StaggerContainer className="grid grid-cols-2 gap-6" data-id="element-38">
          {[{
          icon: Zap,
          text: 'Faster Turnaround Times'
        }, {
          icon: Truck,
          text: 'Lower Shipping Costs'
        }, {
          icon: Package,
          text: 'Reduced Warehouse Needs'
        }, {
          icon: DollarSign,
          text: 'Less Capital in Inventory'
        }, {
          icon: Clock,
          text: 'Same-Day Sourcing'
        }, {
          icon: TrendingUp,
          text: 'Higher Operating Efficiency'
        }].map((benefit, i) => <StaggerItem key={i} data-id="element-39">
              <Card className="p-8 flex flex-col items-center text-center h-full justify-center" data-id="element-40">
                <benefit.icon className="w-10 h-10 text-black mb-6" strokeWidth={1.5} data-id="element-41" />
                <span className="text-black font-bold uppercase tracking-wide" data-id="element-42">
                  {benefit.text}
                </span>
              </Card>
            </StaggerItem>)}
        </StaggerContainer>
      </div>
    </div>
  </Slide>;
export const Slide12RolloutPlan = () => {
  const months = [{
    m: 'Month 1',
    rev: '$5K',
    height: 'h-12',
    note: 'We are here'
  }, {
    m: 'Month 2',
    rev: '$8K',
    height: 'h-16'
  }, {
    m: 'Month 3',
    rev: '$15K',
    height: 'h-28'
  }, {
    m: 'Month 4',
    rev: '$22K',
    height: 'h-40'
  }, {
    m: 'Month 5',
    rev: '$32K',
    height: 'h-56'
  }, {
    m: 'Month 6',
    rev: '$45K+',
    height: 'h-72'
  }];
  return <Slide id="slide-12" bg="gray" data-id="element-43">
      <SlideHeading tag="6-Month Rollout Plan" title="Activating the Engine" subtitle="Realistic, grounded milestones to scale revenue from our current baseline." data-id="element-44" />

      <div className="mt-16 relative" data-id="element-45">
        <div className="absolute bottom-12 left-0 w-full h-1 bg-black z-0" data-id="element-46" />

        <StaggerContainer className="flex justify-between items-end h-96 relative z-10" data-id="element-47">
          {months.map((month, i) => <StaggerItem key={i} className="flex flex-col items-center w-full group" data-id="element-48">
              {month.note && <div className="bg-black text-white text-xs font-bold px-3 py-1 mb-2 uppercase tracking-widest whitespace-nowrap" data-id="element-49">
                  {month.note}
                </div>}
              <div className="text-3xl md:text-4xl font-serif font-bold text-black mb-4" data-id="element-50">
                {month.rev}
              </div>
              <div className={`w-full max-w-[80px] bg-black ${month.height} transition-all duration-500 group-hover:bg-slate-800`} data-id="element-51" />
              <div className="mt-6 text-slate-600 font-bold uppercase tracking-widest text-sm md:text-base" data-id="element-52">
                {month.m}
              </div>
              <div className="absolute bottom-10 w-5 h-5 bg-white border-4 border-black" data-id="element-53" />
            </StaggerItem>)}
        </StaggerContainer>
      </div>
    </Slide>;
};
export const Slide13FinancialModel = () => {
  const data = [{
    name: 'M1',
    revenue: 5000
  }, {
    name: 'M2',
    revenue: 8000
  }, {
    name: 'M3',
    revenue: 15000
  }, {
    name: 'M4',
    revenue: 22000
  }, {
    name: 'M5',
    revenue: 32000
  }, {
    name: 'M6',
    revenue: 45000
  }];
  return <Slide id="slide-13" bg="white" data-id="element-54">
      <SlideHeading tag="Financial Growth Model" title="Projected Trajectory" subtitle="Based on active infrastructure and capital injection." data-id="element-55" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-8" data-id="element-56">
        <FadeIn delay={0.2} className="lg:col-span-2 h-[400px] w-full border-4 border-black p-6 bg-white" data-id="element-57">
          <ResponsiveContainer width="100%" height="100%" data-id="element-58">
            <AreaChart data={data} margin={{
            top: 20,
            right: 30,
            left: 0,
            bottom: 0
          }} data-id="element-59">
              <defs data-id="element-60">
                <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1" data-id="element-61">
                  <stop offset="5%" stopColor="#000000" stopOpacity={0.2} data-id="element-62" />
                  <stop offset="95%" stopColor="#000000" stopOpacity={0} data-id="element-63" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} data-id="element-64" />
              <XAxis dataKey="name" stroke="#000000" tick={{
              fill: '#000000',
              fontWeight: 'bold'
            }} axisLine={{
              stroke: '#000000',
              strokeWidth: 2
            }} tickLine={false} data-id="element-65" />
              <YAxis stroke="#000000" tick={{
              fill: '#000000',
              fontWeight: 'bold'
            }} axisLine={{
              stroke: '#000000',
              strokeWidth: 2
            }} tickLine={false} tickFormatter={value => `$${value / 1000}k`} data-id="element-66" />
              <Tooltip contentStyle={{
              backgroundColor: '#ffffff',
              borderColor: '#000000',
              borderWidth: '2px',
              borderRadius: '0px',
              color: '#000000',
              fontWeight: 'bold'
            }} itemStyle={{
              color: '#000000'
            }} formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']} data-id="element-67" />
              <Area type="monotone" dataKey="revenue" stroke="#000000" strokeWidth={4} fillOpacity={1} fill="url(#colorRev)" data-id="element-68" />
            </AreaChart>
          </ResponsiveContainer>
        </FadeIn>

        <div className="flex flex-col gap-6" data-id="element-69">
          <h3 className="text-2xl font-serif font-bold text-black uppercase mb-2" data-id="element-70">
            Year 1 Scenarios
          </h3>
          {[{
          label: 'Conservative',
          value: '$250K',
          bg: 'bg-white',
          text: 'text-black'
        }, {
          label: 'Strong Execution',
          value: '$450K',
          bg: 'bg-slate-100',
          text: 'text-black'
        }, {
          label: 'Aggressive',
          value: '$700K+',
          bg: 'bg-black',
          text: 'text-white'
        }].map((scenario, i) => <FadeIn key={i} delay={0.4 + i * 0.1} data-id="element-71">
              <div className={`border-2 border-black p-8 ${scenario.bg} ${scenario.text}`} data-id="element-72">
                <div className="text-sm font-bold uppercase tracking-widest mb-2 opacity-80" data-id="element-73">
                  {scenario.label}
                </div>
                <div className="text-5xl font-serif font-bold" data-id="element-74">
                  {scenario.value}
                </div>
              </div>
            </FadeIn>)}
        </div>
      </div>

      {/* Unit Economics */}
      <FadeIn delay={0.4} className="mt-16 mb-6" data-id="element-75">
        <h3 className="text-3xl font-serif font-bold text-black uppercase" data-id="element-76">
          Ultra-Conservative Unit Economics
        </h3>
        <p className="text-slate-500 text-lg mt-2" data-id="element-77">
          These numbers are intentionally modest. Based on founder experience,
          the real ceiling is orders of magnitude higher.
        </p>
      </FadeIn>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-78">
        <FadeIn delay={0.5} data-id="element-79">
          <div className="border-4 border-black p-8 bg-white text-center" data-id="element-80">
            <div className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3" data-id="element-81">
              Month 2 — Breakeven
            </div>
            <div className="text-6xl font-serif font-bold text-black leading-none mb-2" data-id="element-82">
              35
            </div>
            <div className="text-lg font-bold uppercase text-slate-600" data-id="element-83">
              Shirts / Day
            </div>
            <div className="h-0.5 w-12 bg-black mx-auto my-4" data-id="element-84" />
            <p className="text-slate-500 text-sm" data-id="element-85">
              Covers all fixed costs: salary + rent. One operator.
            </p>
          </div>
        </FadeIn>
        <FadeIn delay={0.6} data-id="element-86">
          <div className="border-4 border-black p-8 bg-black text-center text-white" data-id="element-87">
            <div className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-3" data-id="element-88">
              Month 5 — Conservative Target
            </div>
            <div className="text-6xl font-serif font-bold text-white leading-none mb-2" data-id="element-89">
              60
            </div>
            <div className="text-lg font-bold uppercase text-slate-300" data-id="element-90">
              Shirts / Day
            </div>
            <div className="h-0.5 w-12 bg-white mx-auto my-4" data-id="element-91" />
            <p className="text-slate-400 text-sm" data-id="element-92">
              $10 margin each = $18,000/month net profit
            </p>
          </div>
        </FadeIn>
        <FadeIn delay={0.7} data-id="element-93">
          <div className="border-4 border-black p-8 bg-white text-center" data-id="element-94">
            <div className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3" data-id="element-95">
              Proven Founder Capacity
            </div>
            <div className="text-6xl font-serif font-bold text-black leading-none mb-2" data-id="element-96">
              3,000
            </div>
            <div className="text-lg font-bold uppercase text-slate-600" data-id="element-97">
              Shirts / Day
            </div>
            <div className="h-0.5 w-12 bg-black mx-auto my-4" data-id="element-98" />
            <p className="text-slate-500 text-sm" data-id="element-99">
              DTC volume Santiago managed at his previous operation. This is not
              theory.
            </p>
          </div>
        </FadeIn>
      </div>
    </Slide>;
};
export const Slide14UseOfFunds = () => {
  const funds = [{
    name: 'Customer Acquisition & Ads',
    value: '20%'
  }, {
    name: 'Sales Systems & B2B Leads',
    value: '15%'
  }, {
    name: 'Remote Team Expansion',
    value: '15%'
  }, {
    name: 'Working Capital & Ops',
    value: '10%'
  }, {
    name: 'Marketplace Growth',
    value: '10%'
  }, {
    name: 'Equipment Optimization',
    value: '10%'
  }, {
    name: 'Content Scale',
    value: '5%'
  }, {
    name: 'Ink Product Dev',
    value: '5%'
  }, {
    name: 'Operating Reserve & Opportunity Fund',
    value: '10%'
  }];
  return <Slide id="slide-14" bg="gray" data-id="element-100">
      <SlideHeading tag="The Ask" title="$100K Growth Round" subtitle="Fueling an already-built platform. Every dollar goes to acceleration." data-id="element-101" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-8" data-id="element-102">
        <FadeIn delay={0.2} data-id="element-103">
          <Card className="h-full p-10" data-id="element-104">
            <h3 className="text-3xl font-serif font-bold text-black uppercase mb-8" data-id="element-105">
              Use of Funds
            </h3>
            <div className="space-y-4" data-id="element-106">
              {funds.map((item, i) => <div key={i} className="flex items-center justify-between p-4 bg-white border-2 border-black" data-id="element-107">
                  <span className="text-black font-bold uppercase tracking-wide text-sm md:text-base" data-id="element-108">
                    {item.name}
                  </span>
                  <span className="text-black font-serif font-bold text-xl" data-id="element-109">
                    {item.value}
                  </span>
                </div>)}
            </div>
          </Card>
        </FadeIn>

        <FadeIn delay={0.4} data-id="element-110">
          <Card dark className="h-full p-10 flex flex-col" data-id="element-111">
            <Handshake className="w-12 h-12 text-white mb-8" data-id="element-112" />
            <h3 className="text-3xl font-serif font-bold text-white uppercase mb-8" data-id="element-113">
              Deal Structure
            </h3>
            <div className="flex-grow flex flex-col justify-center" data-id="element-114">
              <div className="border-2 border-dashed border-slate-600 p-8 text-center bg-slate-900" data-id="element-115">
                <p className="text-slate-300 text-lg font-medium leading-relaxed mb-6" data-id="element-116">
                  [ To be discussed with investor ]
                </p>
                <p className="text-slate-400 text-base" data-id="element-117">
                  Equity percentage, return structure, and involvement level to
                  be determined based on investor commitment.
                </p>
              </div>
            </div>
          </Card>
        </FadeIn>
      </div>
    </Slide>;
};